import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AdsClickOutlinedIcon from "@mui/icons-material/AdsClickOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import { fetchData } from "../../api/client";
import ConfirmDialog from "../../components/common/ConfirmDialog";

const BRL = (n) =>
  Number(n || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const DEFAULT_EXAMPLES = [
  "Olá! Vim pelo anúncio de passagens e quero saber os horários disponíveis.",
  "Oi, vi a promoção no Google e gostaria de mais informações.",
  "Olá! Quero falar sobre a oferta que apareceu no anúncio.",
];

function GuideCard({
  icon,
  title,
  summary,
  children,
  defaultOpen = false,
  open: openProp,
  onOpenChange,
  accent = "primary",
}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const controlled = openProp !== undefined;
  const open = controlled ? openProp : uncontrolledOpen;
  const setOpen = (next) => {
    if (!controlled) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };
  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: 2,
        bgcolor: "background.paper",
      }}
    >
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="flex-start"
        sx={{ cursor: "pointer" }}
        onClick={() => setOpen(!open)}
      >
        <Box sx={{ color: `${accent}.main`, mt: 0.25, display: "flex" }}>{icon}</Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.55 }}>
            {summary}
          </Typography>
        </Box>
        {open ? <ExpandLessIcon color="action" /> : <ExpandMoreIcon color="action" />}
      </Stack>
      <Collapse in={open}>
        <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid", borderColor: "divider" }}>{children}</Box>
      </Collapse>
    </Paper>
  );
}

function StepList({ steps }) {
  return (
    <Stack spacing={1.25} component="ol" sx={{ m: 0, pl: 2.25 }}>
      {steps.map((step) => (
        <Typography key={step} component="li" variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
          {step}
        </Typography>
      ))}
    </Stack>
  );
}

function AdOriginCreateDialog({ open, onClose, onSaved, examples, channels, editItem = null }) {
  const isEdit = Boolean(editItem?.id);
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [originId, setOriginId] = useState("");
  const [budget, setBudget] = useState("");
  const [aiBrief, setAiBrief] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [error, setError] = useState("");
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(editItem?.name || "");
    setMessage(editItem?.starterMessage || "");
    setOriginId(editItem?.originId != null ? String(editItem.originId) : "");
    setBudget(
      editItem?.budget != null && Number.isFinite(Number(editItem.budget))
        ? String(editItem.budget)
        : ""
    );
    setAiBrief("");
    setSuggestions([]);
    setError("");
    setCreated(null);
    setCopied(false);
    setBusy(false);
    setAiBusy(false);
  }, [open, editItem]);

  const sampleList = examples?.length ? examples : DEFAULT_EXAMPLES;

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError("Não foi possível copiar. Selecione o texto e copie manualmente.");
    }
  };

  const suggestAi = async () => {
    setAiBusy(true);
    setError("");
    try {
      const res = await fetchData("/campaigns/ad-origins/ai-suggest", "POST", {
        instruction: aiBrief.trim(),
      });
      const list = Array.isArray(res?.data?.suggestions) ? res.data.suggestions : [];
      setSuggestions(list);
      if (list[0] && !message.trim()) setMessage(list[0]);
    } catch (e) {
      setError(e?.message || "Não foi possível gerar sugestões.");
    } finally {
      setAiBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    setError("");
    try {
      const payload = {
        name: name.trim(),
        starterMessage: message.trim(),
        originId: originId || null,
        budget: budget === "" ? null : Number(budget),
      };
      const res = isEdit
        ? await fetchData(`/campaigns/ad-origins/${editItem.id}`, "PATCH", payload)
        : await fetchData("/campaigns/ad-origins", "POST", payload);
      const item = res?.data?.item;
      if (isEdit) {
        onSaved?.(item);
        onClose?.();
      } else {
        setCreated(item);
        onSaved?.(item);
      }
    } catch (e) {
      setError(e?.message || "Não foi possível salvar a origem.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700 }}>
        {created
          ? "Mensagem pronta para o Google"
          : isEdit
            ? "Editar origem de anúncio"
            : "Criar mensagem para o Google"}
      </DialogTitle>
      <DialogContent dividers>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        {created ? (
          <Stack spacing={2}>
            <Alert severity="success" icon={<CheckCircleOutlineIcon />}>
              Origem <strong>{created.name}</strong> criada. Agora cole esta mensagem no anúncio do
              Google — sem mudar nenhuma palavra.
            </Alert>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: "action.hover" }}>
              <Typography variant="caption" color="text.secondary">
                Mensagem inicial (copie exatamente)
              </Typography>
              <Typography variant="body1" sx={{ mt: 0.75, whiteSpace: "pre-wrap", fontWeight: 600 }}>
                {created.starterMessage}
              </Typography>
              <Button
                sx={{ mt: 1.5 }}
                size="small"
                startIcon={<ContentCopyIcon />}
                onClick={() => copyText(created.starterMessage)}
              >
                {copied ? "Copiado!" : "Copiar mensagem"}
              </Button>
            </Paper>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Como usar no Google Ads
              </Typography>
              <StepList
                steps={[
                  "Abra o anúncio no Google Ads que leva para o WhatsApp.",
                  "Na mensagem pré-preenchida (ou texto do clique para WhatsApp), cole a frase acima.",
                  "Não altere espaços, emojis ou palavras — o XPro compara o texto completo.",
                  "Publique o anúncio e faça um teste clicando nele com um celular.",
                  "A conversa deve aparecer em Dashboard → Campanhas, marcada como Google · mensagem.",
                ]}
              />
            </Box>
          </Stack>
        ) : (
          <Stack spacing={2.25}>
            {!isEdit ? (
              <Alert severity="info">
                O Google não avisa o XPro qual anúncio gerou a conversa. Por isso usamos uma{" "}
                <strong>frase combinada</strong>: o cliente envia essa mensagem ao abrir o WhatsApp, e
                o XPro reconhece a campanha.
              </Alert>
            ) : null}

            <TextField
              label="Nome desta origem"
              placeholder="Ex.: Promoção feriado — Google"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              helperText="Só para você identificar no relatório. O cliente não vê este nome."
            />

            <TextField
              label="Mensagem inicial"
              placeholder="A frase que o cliente vai enviar ao clicar no anúncio"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              fullWidth
              multiline
              minRows={3}
              helperText="Use uma frase específica desta campanha (produto, promoção ou cidade)."
            />

            <TextField
              fullWidth
              type="number"
              label="Orçamento (R$)"
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
              inputProps={{ min: 0, step: "0.01" }}
              helperText="Opcional. Usado no Dashboard → Campanhas para calcular custo por conversa e por venda desta origem."
            />

            <Alert severity="info" sx={{ py: 0.75 }}>
              <Typography variant="body2" sx={{ lineHeight: 1.55 }}>
                Informe o investimento desta campanha de anúncio para acompanhar custo por
                conversa e por venda no dashboard. Sem orçamento cadastrado, esses custos não
                aparecem.
              </Typography>
            </Alert>

            {!isEdit ? (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
                  Exemplos — toque para usar
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={0.75}>
                  {sampleList.map((ex) => (
                    <Chip
                      key={ex}
                      size="small"
                      label={ex.length > 48 ? `${ex.slice(0, 48)}…` : ex}
                      onClick={() => setMessage(ex)}
                      variant="outlined"
                    />
                  ))}
                </Stack>
              </Box>
            ) : null}

            {!isEdit ? (
              <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <AutoAwesomeIcon fontSize="small" color="primary" />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Sugerir com IA
                  </Typography>
                </Stack>
                <TextField
                  size="small"
                  fullWidth
                  label="O que o anúncio oferece?"
                  placeholder="Ex.: passagens promocionais São Paulo–Rio no feriado"
                  value={aiBrief}
                  onChange={(e) => setAiBrief(e.target.value)}
                />
                <Button
                  sx={{ mt: 1.25 }}
                  size="small"
                  variant="outlined"
                  disabled={aiBusy || !aiBrief.trim()}
                  startIcon={aiBusy ? <CircularProgress size={14} /> : <AutoAwesomeIcon />}
                  onClick={suggestAi}
                >
                  Gerar 3 opções
                </Button>
                {suggestions.length ? (
                  <Stack spacing={0.75} sx={{ mt: 1.25 }}>
                    {suggestions.map((s) => (
                      <Chip
                        key={s}
                        label={s}
                        onClick={() => setMessage(s)}
                        variant={message === s ? "filled" : "outlined"}
                        color={message === s ? "primary" : "default"}
                        sx={{ height: "auto", py: 0.75, "& .MuiChip-label": { whiteSpace: "normal" } }}
                      />
                    ))}
                  </Stack>
                ) : null}
              </Paper>
            ) : null}

            {channels?.length ? (
              <TextField
                select
                label="Canal WhatsApp (opcional)"
                value={originId}
                onChange={(e) => setOriginId(e.target.value)}
                fullWidth
                helperText="Se informar, o rastreio só vale para conversas neste número."
              >
                <MenuItem value="">Qualquer canal WhatsApp</MenuItem>
                {channels.map((ch) => (
                  <MenuItem key={ch.originId} value={String(ch.originId)}>
                    {ch.name || `Canal ${ch.originId}`}
                  </MenuItem>
                ))}
              </TextField>
            ) : null}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={busy}>
          {created ? "Fechar" : "Cancelar"}
        </Button>
        {!created ? (
          <Button
            variant="contained"
            onClick={save}
            disabled={busy || !name.trim() || !message.trim()}
          >
            {busy ? "Salvando…" : isEdit ? "Salvar alterações" : "Salvar mensagem"}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}

export default function AdOriginsPage() {
  const [items, setItems] = useState([]);
  const [examples, setExamples] = useState(DEFAULT_EXAMPLES);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [googleGuideOpen, setGoogleGuideOpen] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [listRes, capsRes] = await Promise.all([
        fetchData("/campaigns/ad-origins", "GET"),
        fetchData("/campaigns/capabilities", "GET").catch(() => null),
      ]);
      setItems(Array.isArray(listRes?.data?.items) ? listRes.data.items : []);
      if (Array.isArray(listRes?.data?.examples) && listRes.data.examples.length) {
        setExamples(listRes.data.examples);
      }
      const capsRaw = capsRes?.data;
      const caps = Array.isArray(capsRaw)
        ? capsRaw
        : capsRaw?.channels || capsRaw?.origins || [];
      const waChannels = (Array.isArray(caps) ? caps : []).filter((c) =>
        String(c.channelKey || c.channel_key || "")
          .toLowerCase()
          .includes("whatsapp")
      );
      setChannels(
        waChannels.map((c) => ({
          originId: c.originId || c.origin_id || c.id,
          name: c.name || c.nome || c.label,
        }))
      );
    } catch (e) {
      setError(e?.message || "Não foi possível carregar as origens.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const activeCount = useMemo(
    () => items.filter((i) => i.status === "active").length,
    [items]
  );

  const patchStatus = async (item, status) => {
    try {
      await fetchData(`/campaigns/ad-origins/${item.id}`, "PATCH", { status });
      await load();
    } catch (e) {
      setError(e?.message || "Não foi possível atualizar.");
    }
  };

  const remove = async () => {
    if (!deleteTarget?.id) return;
    setDeleting(true);
    setError("");
    try {
      await fetchData(`/campaigns/ad-origins/${deleteTarget.id}`, "DELETE");
      setDeleteTarget(null);
      await load();
    } catch (e) {
      setError(e?.message || "Não foi possível remover.");
    } finally {
      setDeleting(false);
    }
  };

  const copyMessage = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      setError("Não foi possível copiar a mensagem.");
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3, maxWidth: 720 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Origem de anúncios
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1, lineHeight: 1.65 }}>
          Aqui você acompanha conversas que nasceram de anúncios. A Meta costuma avisar sozinha; o
          Google precisa de uma mensagem inicial — explicamos os dois caminhos abaixo. Cadastre um
          orçamento em cada origem para ver custo por conversa e por venda no dashboard.
        </Typography>
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      ) : null}

      <Stack spacing={2} sx={{ mb: 3 }}>
        <GuideCard
          icon={<AdsClickOutlinedIcon />}
          title="Meta (Facebook / Instagram) — automático"
          summary="Quando o anúncio abre o WhatsApp, a origem já chega marcada. Em geral você não precisa configurar nada no XPro."
        >
          <StepList
            steps={[
              "Crie o anúncio Click to WhatsApp no Gerenciador de Anúncios da Meta.",
              "Use o mesmo número WhatsApp Business conectado ao XPro.",
              "Quando alguém clicar e conversar, a solicitação aparece com origem de anúncio (CTWA).",
              "Veja o consolidado em Dashboard → Campanhas.",
            ]}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, lineHeight: 1.55 }}>
            Título do anúncio vazio ou inválido é regra da Meta — preencha o título no anúncio para o
            criativo ser aceito. Isso não é configuração do XPro.
          </Typography>
          <Alert severity="info" sx={{ mt: 1.5 }}>
            <Typography variant="body2" sx={{ lineHeight: 1.55 }}>
              <strong>Investimento / custo:</strong> o dashboard usa o orçamento que você cadastrar
              nas origens com mensagem de rastreio (Google · ícone de lápis na lista) para custo por
              conversa e por venda. Conversas Meta entram no volume automaticamente.
            </Typography>
          </Alert>
        </GuideCard>

        <GuideCard
          icon={<CampaignOutlinedIcon />}
          title="Google Ads — mensagem inicial (passo a passo)"
          summary="O Google não envia o “carimbo” do anúncio no WhatsApp. Por isso criamos uma frase única e o XPro reconhece a campanha quando o cliente manda essa frase."
          open={googleGuideOpen}
          onOpenChange={setGoogleGuideOpen}
          accent="warning"
        >
          <Alert severity="warning" sx={{ mb: 2 }}>
            Pense assim: a mensagem é um <strong>código secreto legível</strong>. Se o cliente
            enviar exatamente a mesma frase do anúncio, sabemos de qual campanha veio.
          </Alert>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Em 4 passos
          </Typography>
          <StepList
            steps={[
              "Clique em “Criar mensagem para o Google” e dê um nome fácil (ex.: Oferta março — Google).",
              "Escreva ou peça à IA uma frase específica desta campanha.",
              "Copie a frase e cole na mensagem pré-preenchida do anúncio no Google Ads.",
              "Teste clicando no anúncio: a conversa deve aparecer ligada a essa origem no Dashboard → Campanhas (selo Google · mensagem).",
            ]}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, mb: 2, lineHeight: 1.55 }}>
            Importante: se alguém editar a frase no Google (ou o cliente apagar parte dela), o
            rastreio não casa. Mantenha a frase idêntica.
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ lineHeight: 1.55 }}>
              <strong>Investimento / custo:</strong> na criação ou edição da mensagem você pode
              cadastrar o orçamento da campanha. Esse valor é usado no Dashboard → Campanhas para
              custo por conversa e por venda.
            </Typography>
          </Alert>
          <Button
            variant="contained"
            onClick={() => {
              setGoogleGuideOpen(true);
              setCreateOpen(true);
            }}
          >
            Criar mensagem para o Google
          </Button>
        </GuideCard>
      </Stack>

      <Divider sx={{ my: 3 }} />

      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Suas mensagens de rastreio
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {activeCount
              ? `${activeCount} ativa(s) · cadastre o orçamento para ver custos no dashboard`
              : "Nenhuma mensagem ativa ainda. Crie a primeira para rastrear Google Ads."}
          </Typography>
        </Box>
        <Button variant="outlined" onClick={() => setCreateOpen(true)}>
          Nova mensagem
        </Button>
      </Stack>

      {loading ? (
        <Stack alignItems="center" sx={{ py: 4 }}>
          <CircularProgress size={28} />
        </Stack>
      ) : !items.length ? (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            Quando você criar uma mensagem, ela aparece aqui com botão de copiar para colar no
            Google Ads. Conversas rastreadas ficam no Dashboard → Campanhas.
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={1.5}>
          {items.map((item) => (
            <Paper key={item.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.5}
                alignItems={{ md: "flex-start" }}
                justifyContent="space-between"
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {item.name}
                    </Typography>
                    <Chip
                      size="small"
                      label={item.status === "active" ? "Ativa" : "Pausada"}
                      color={item.status === "active" ? "success" : "default"}
                      variant="outlined"
                    />
                    {typeof item.conversationCount === "number" ? (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`${item.conversationCount} conversa(s)`}
                      />
                    ) : null}
                    {item.budget != null && Number(item.budget) > 0 ? (
                      <Tooltip title="Orçamento usado no Dashboard → Campanhas para custo por conversa e por venda.">
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`Orçamento ${BRL(item.budget)}`}
                        />
                      </Tooltip>
                    ) : null}
                  </Stack>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.75, whiteSpace: "pre-wrap", lineHeight: 1.5 }}
                  >
                    {item.starterMessage}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Tooltip title="Editar">
                    <IconButton onClick={() => setEditItem(item)} size="small">
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Copiar mensagem">
                    <IconButton onClick={() => copyMessage(item.starterMessage)} size="small">
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={item.status === "active" ? "Pausar" : "Reativar"}>
                    <IconButton
                      size="small"
                      onClick={() =>
                        patchStatus(item, item.status === "active" ? "paused" : "active")
                      }
                    >
                      <PauseCircleOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Remover">
                    <IconButton size="small" onClick={() => setDeleteTarget(item)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}

      <AdOriginCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSaved={() => load()}
        examples={examples}
        channels={channels}
      />

      <AdOriginCreateDialog
        open={Boolean(editItem)}
        editItem={editItem}
        onClose={() => setEditItem(null)}
        onSaved={() => load()}
        examples={examples}
        channels={channels}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title={`Remover a origem “${deleteTarget?.name || ""}”?`}
        content="Conversas já rastreadas permanecem no histórico."
        confirmText="Remover"
        cancelText="Cancelar"
        confirmColor="error"
        loading={deleting}
        onClose={() => (deleting ? null : setDeleteTarget(null))}
        onConfirm={remove}
      />
    </Box>
  );
}
